import { useState, useRef, Fragment, useEffect } from "react";
import Popup from "../../../hoc/Popup/Popup";
import {
  IRehabBooking,
  IRehabSummary,
  IRehabSummaryItem,
  IUser,
} from "../../../types/api";
import "./AdminRehabSlotPopup.scss";
import { formatDateToSwedish } from "../../../shared/utility";
import greenCheck from "../../../assets/images/green-check.svg";
import { CognitoUser } from "../../../types/cognito";
import { toast } from "react-toastify";
import {
  cancelRehabBooking,
  createRehabBooking,
  getActiveRehabBookingsByMember,
} from "../../../api/rehab/rehab";
import { formatDate } from "../../../shared/dateUtils";
import { getUserByMemberId } from "../../../api/users/users";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";

interface IAdminRehabSlotPopupProps {
  onClose: () => void;
  showPopup: boolean;
  onConfirm: () => void;
  rehabSummaryItem?: IRehabSummaryItem;
  selectedUser?: IUser | null;
  date: Date;
}

function AdminRehabSlotPopup(props: IAdminRehabSlotPopupProps) {
  const {
    rehabSummaryItem,
    date,
    showPopup,
    onClose,
    onConfirm,
    selectedUser,
  } = props;
  const [isReady, setIsReady] = useState(false);
  const { weekday, dateAndMonth } = formatDateToSwedish(date);
  const [hasBooking, setHasBooking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeRehabBookingsForMember, setActiveRehabBookingsForMember] =
    useState<IRehabBooking[]>([]);

  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [userData, setUserData] = useState<IUser>();
  const [sendEmail, setSendEmail] = useState(true);
  const handleClose = () => {
    setIsReady(false);
    onClose();
  };
  const handleConfirmBooking = async () => {
    if (rehabSummaryItem === undefined || !selectedUser) {
      return;
    }

    setIsLoading(true);
    const loadToast = toast.loading("Bokar tid...");

    const bookingToCreate = {
      datetime_start: `${formatDate(date, "yyyy-MM-dd")}T${
        rehabSummaryItem?.start_time
      }`,
      datetime_end: `${formatDate(date, "yyyy-MM-dd")}T${
        getEndTime(rehabSummaryItem?.start_time) + ":00"
      }`,
      flag: 1,
      member_id: selectedUser.member_id || "",
    };

    if (bookingToCreate.member_id === "") {
      setIsLoading(false);
      toast.dismiss(loadToast);
      return;
    }
    try {
      const response = await createRehabBooking(
        bookingToCreate,
        sendEmail,
        true
      );
      if (response && !("message" in response)) {
        setIsLoading(false);
        toast.dismiss(loadToast);
        toast.success("Rehab tid bokad");
        onConfirm();
        handleClose();
      }
    } catch (error: any) {
      setIsLoading(false);
      toast.dismiss(loadToast);
      toast.error(error);
    }
  };

  const getEndTime = (startTime: string) => {
    const hours = parseInt(startTime.slice(0, 2));
    const minutes = parseInt(startTime.slice(3, 5));

    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    date.setMinutes(date.getMinutes() + 15);

    const newHours = date.getHours().toString().padStart(2, "0");
    const newMinutes = date.getMinutes().toString().padStart(2, "0");

    return `${newHours}:${newMinutes}`;
  };

  const handleCancelRehabBooking = async (rehabBooking: IRehabBooking) => {
    setIsLoading(true);
    const loadToast = toast.loading("Avbokar tid...");
    if (rehabBooking === undefined) {
      setIsLoading(false);
      toast.dismiss(loadToast);
      return;
    }

    try {
      const response = await cancelRehabBooking(rehabBooking, sendEmail, true);
      if (response && !("messsage" in response)) {
        toast.success("Rehab avbokad");
        setIsLoading(false);
        toast.dismiss(loadToast);
        onConfirm();
        handleClose();
      }
    } catch (error: any) {
      setIsLoading(false);
      toast.error(error);
    }
  };
  const fetchActiveRehabBookingsForMember = async () => {
    if (selectedUser === null || selectedUser === undefined) {
      return;
    }
    try {
      const response = await getActiveRehabBookingsByMember(
        selectedUser.member_id,
        true
      );
      setActiveRehabBookingsForMember(response);
    } catch (error: any) {
      toast.error(error.errors[0].message);
    }
  };
  useEffect(() => {
    if (selectedUser !== null && showPopup) {
      fetchActiveRehabBookingsForMember();
    }
  }, [selectedUser, showPopup]);
  const handleFetchBookerData = async (memberId: string) => {
    const response = await getUserByMemberId(memberId);
    setUserData(response);
  };

  useEffect(() => {
    if (props.showPopup === true && rehabSummaryItem) {
      setHasBooking(
        rehabSummaryItem?.booking !== null &&
          rehabSummaryItem?.booking.member_id !== ""
          ? true
          : false
      );
      if (
        rehabSummaryItem?.booking !== null &&
        rehabSummaryItem?.booking.member_id !== ""
      ) {
        handleFetchBookerData(rehabSummaryItem?.booking.member_id);
      }
    }
  }, [props.showPopup]);

  return (
    <>
      <Popup
        showPopup={showPopup}
        onClose={handleClose}
        leftTopElement={
          <span className="text-m">
            {hasBooking ? "Bokad tid" : "Boka 15 minuters Rehab-bad"}
          </span>
        }
      >
        <div className="general-popup-wrapper">
          <form
            className="general-popup-form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="general-popup-form-top">
              {!hasBooking && selectedUser && (
                <button
                  className="text-m"
                  onClick={() => {
                    if (activeRehabBookingsForMember.length > 0) {
                      setShowConfirmationPopup(true);
                      return;
                    } else if (!isLoading) {
                      handleConfirmBooking();
                    }
                  }}
                >
                  Boka tid
                </button>
              )}
            </div>
            <div className="general-popup-form-group mb-20">
              <div className="general-popup-form-field">
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Datum</span>
                  <div className="text-m primary-text text-svg-wrapper">
                    <span>
                      {weekday}, {dateAndMonth}
                    </span>
                    <img src={greenCheck} alt="green check"></img>
                  </div>
                </div>
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Vald tid</span>
                  <div className="text-m primary-text text-svg-wrapper">
                    <span>
                      {rehabSummaryItem
                        ? rehabSummaryItem.start_time.slice(0, 5) +
                          " - " +
                          getEndTime(rehabSummaryItem.start_time).slice(0, 5)
                        : ""}
                    </span>
                    <img src={greenCheck} alt="green check"></img>
                  </div>
                </div>
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Kommentar</span>
                  <span className="text-m">
                    {rehabSummaryItem?.comment || "-"}
                  </span>
                </div>
                {hasBooking && userData ? (
                  <div className="general-popup-flex-between">
                    <span className="text-m primary-text">
                      Medlem på denna bokning
                    </span>
                    <div className="text-m primary-text text-svg-wrapper">
                      <span>
                        {userData.first_name +
                          " " +
                          userData.last_name +
                          " (" +
                          userData.member_id +
                          ")"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="general-popup-flex-between">
                    <span className="text-m primary-text">Vald medlem</span>
                    <div className="text-m primary-text text-svg-wrapper">
                      <span>
                        {selectedUser
                          ? selectedUser.first_name +
                            " " +
                            selectedUser.last_name +
                            " (" +
                            selectedUser.member_id +
                            ")"
                          : ""}
                      </span>
                    </div>
                  </div>
                )}

                {rehabSummaryItem && hasBooking && (
                  <div className="general-popup-flex-right">
                    <a
                      className="text-m danger"
                      onClick={() => {
                        handleCancelRehabBooking(rehabSummaryItem.booking);
                      }}
                    >
                      Avboka
                    </a>
                  </div>
                )}
                <div className="general-popup-flex-between">
                  <span className="text-m secondary-text">
                    Skicka Email vid ändring
                  </span>
                  <label className="toggle text-m">
                    <input
                      type="checkbox"
                      name="closed"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(!sendEmail)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Popup>
      <HandleConfirmationPopup
        showPopup={showConfirmationPopup}
        onClose={() => setShowConfirmationPopup(false)}
        onConfirm={() => {
          handleConfirmBooking();
          setShowConfirmationPopup(false);
        }}
        questionText="Användaren har en aktiv Rehab-bads bokning, vill du boka ändå?"
        confirmText="Boka"
        denyText="Avbryt"
      />
    </>
  );  
}

export default AdminRehabSlotPopup;
